import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import type { ProjectResponse, TrackResponse } from '@smplecut/shared';

// A single actionable row in the palette — either a command the user invokes
// in place (toggle mode, sign out, navigate) or a content result resolved
// from the projects/tracks/regions index.
export interface PaletteItem {
  id: string;
  label: string;
  detail?: string;
  group: 'Navigate' | 'Create' | 'Appearance' | 'Account' | 'Legal' | 'Projects' | 'Tracks' | 'Regions';
  icon: string; // 1-2 char glyph
  iconClass: string;
  keywords?: string[];
  run: () => void;
}

interface Props {
  onClose: () => void;
  onNavigate: (href: string) => void;
  onOpenShortcuts: () => void;
}

export function CommandPalette({ onClose, onNavigate, onOpenShortcuts }: Props) {
  const { logout } = useAuth();
  const { theme, setTheme, mode, toggleMode } = useTheme();

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [contentResults, setContentResults] = useState<PaletteItem[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cacheRef = useRef<{ projects: ProjectResponse[]; tracks: Map<string, TrackResponse[]> }>({
    projects: [],
    tracks: new Map(),
  });

  // Static commands — recomputed when auth or theme state changes so items
  // like "Switch to light mode" / "Theme: Obsidian" reflect current state.
  const commands: PaletteItem[] = useMemo(() => {
    const go = (href: string) => () => { onNavigate(href); onClose(); };
    const list: PaletteItem[] = [
      { id: 'nav-home', label: 'Go to Dashboard', group: 'Navigate', icon: '⌂', iconClass: 'cmd-icon-nav', keywords: ['home'], run: go('/') },
      { id: 'nav-projects', label: 'Go to Projects', group: 'Navigate', icon: '⊟', iconClass: 'cmd-icon-nav', run: go('/projects') },
      { id: 'nav-library', label: 'Go to Library', group: 'Navigate', icon: '♪', iconClass: 'cmd-icon-nav', keywords: ['audio', 'loops', 'samples'], run: go('/library') },
      { id: 'nav-friends', label: 'Go to Friends', group: 'Navigate', icon: '◈', iconClass: 'cmd-icon-nav', keywords: ['collaborators', 'people'], run: go('/friends') },
      { id: 'nav-invites', label: 'Go to Invites', group: 'Navigate', icon: '✉', iconClass: 'cmd-icon-nav', run: go('/invites') },
      { id: 'nav-account', label: 'Go to Account', group: 'Navigate', icon: '●', iconClass: 'cmd-icon-nav', keywords: ['settings', 'profile', 'email', 'password'], run: go('/account') },
      { id: 'nav-changelog', label: 'Go to Changelog', group: 'Navigate', icon: '◷', iconClass: 'cmd-icon-nav', keywords: ['updates', 'whats new'], run: go('/changelog') },
      { id: 'nav-pricing', label: 'Go to Pricing', group: 'Navigate', icon: '¤', iconClass: 'cmd-icon-nav', run: go('/pricing') },

      { id: 'mode-toggle', label: `Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`, group: 'Appearance', icon: mode === 'dark' ? '☀' : '☾', iconClass: 'cmd-icon-appearance', keywords: ['theme', 'color'], run: () => { toggleMode(); onClose(); } },
      ...THEMES.map((t) => ({
        id: `theme-${t.id}`,
        label: `Theme: ${t.label}`,
        detail: theme === t.id ? 'active' : undefined,
        group: 'Appearance' as const,
        icon: '◉',
        iconClass: 'cmd-icon-appearance',
        keywords: ['color', 'palette'],
        run: () => { setTheme(t.id); onClose(); },
      })),

      { id: 'open-shortcuts', label: 'Open keyboard shortcuts', group: 'Account', icon: '⌨', iconClass: 'cmd-icon-account', keywords: ['help', 'keys'], run: () => { onOpenShortcuts(); onClose(); } },
      { id: 'signout', label: 'Sign out', group: 'Account', icon: '⎋', iconClass: 'cmd-icon-account', keywords: ['logout'], run: () => { logout(); onClose(); onNavigate('/'); } },

      { id: 'nav-privacy', label: 'Privacy Policy', group: 'Legal', icon: '§', iconClass: 'cmd-icon-legal', run: go('/privacy') },
      { id: 'nav-terms', label: 'Terms of Service', group: 'Legal', icon: '§', iconClass: 'cmd-icon-legal', run: go('/terms') },
      { id: 'nav-dmca', label: 'DMCA', group: 'Legal', icon: '§', iconClass: 'cmd-icon-legal', run: go('/dmca') },
      { id: 'nav-credits', label: 'Credits & Attributions', group: 'Legal', icon: '§', iconClass: 'cmd-icon-legal', keywords: ['attribution', 'license', 'kevin macleod'], run: go('/credits') },
    ];
    return list;
  }, [mode, theme, setTheme, toggleMode, logout, onClose, onNavigate, onOpenShortcuts]);

  // Focus the input on mount and preload project index for content search.
  useEffect(() => {
    inputRef.current?.focus();
    api.listProjects().then((r) => { cacheRef.current.projects = r.projects; }).catch(() => {});
  }, []);

  // Esc closes.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Debounced content search — mirrors the prior SearchModal behaviour so
  // typing a project/track/region name still resolves to its editor URL.
  useEffect(() => {
    const q = query.trim();
    if (!q) { setContentResults([]); return; }
    setContentLoading(true);
    const handle = setTimeout(async () => {
      const lq = q.toLowerCase();
      const found: PaletteItem[] = [];
      const go = (href: string) => () => { onNavigate(href); onClose(); };

      for (const p of cacheRef.current.projects) {
        if (p.title.toLowerCase().includes(lq)) {
          found.push({
            id: `project-${p.id}`,
            label: p.title,
            detail: `${p.trackCount} track${p.trackCount === 1 ? '' : 's'}`,
            group: 'Projects',
            icon: 'P',
            iconClass: 'cmd-icon-project',
            run: go(`/projects/${p.id}`),
          });
        }
      }
      for (const p of cacheRef.current.projects) {
        if (found.length >= 20) break;
        let tracks = cacheRef.current.tracks.get(p.id);
        if (!tracks) {
          try {
            const r = await api.listTracks(p.id);
            tracks = r.tracks;
            cacheRef.current.tracks.set(p.id, tracks);
          } catch { continue; }
        }
        for (const t of tracks) {
          if (t.title.toLowerCase().includes(lq)) {
            found.push({
              id: `track-${t.id}`,
              label: t.title,
              detail: p.title,
              group: 'Tracks',
              icon: 'T',
              iconClass: 'cmd-icon-track',
              run: go(`/projects/${p.id}/tracks/${t.id}`),
            });
          }
          const meta = t.metadataJson;
          if (meta?.samples) {
            for (const region of meta.samples) {
              const labelMatch = region.label.toLowerCase().includes(lq);
              const tagMatch = region.tags?.some((tag) => tag.toLowerCase().includes(lq));
              if (labelMatch || tagMatch) {
                found.push({
                  id: `region-${t.id}-${region.id}`,
                  label: region.label,
                  detail: `${t.title} / ${p.title}${region.tags?.length ? ` [${region.tags.join(', ')}]` : ''}`,
                  group: 'Regions',
                  icon: 'R',
                  iconClass: 'cmd-icon-region',
                  run: go(`/projects/${p.id}/tracks/${t.id}`),
                });
              }
            }
          }
        }
      }
      setContentResults(found.slice(0, 20));
      setContentLoading(false);
    }, 200);
    return () => clearTimeout(handle);
  }, [query, onNavigate, onClose]);

  // Filter commands against the query using a simple substring match on
  // label + keywords. Fuzzy ranking is tempting but overkill for ~20 rows.
  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => {
      if (c.label.toLowerCase().includes(q)) return true;
      if (c.keywords?.some((k) => k.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [commands, query]);

  // Flatten into a single array so ↑↓ navigation works across sections.
  const rows: PaletteItem[] = useMemo(
    () => [...filteredCommands, ...contentResults],
    [filteredCommands, contentResults],
  );

  // Clamp the selected index whenever the list shrinks.
  useEffect(() => { setSelectedIndex((i) => Math.min(i, Math.max(0, rows.length - 1))); }, [rows.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && rows[selectedIndex]) { e.preventDefault(); rows[selectedIndex].run(); }
  };

  // Group rows for rendering while preserving flat selectedIndex mapping.
  let flatIndex = 0;
  const groups: { name: string; items: { item: PaletteItem; idx: number }[] }[] = [];
  for (const item of rows) {
    let g = groups.find((x) => x.name === item.group);
    if (!g) { g = { name: item.group, items: [] }; groups.push(g); }
    g.items.push({ item, idx: flatIndex++ });
  }

  return (
    <div className="palette-overlay" onMouseDown={onClose}>
      <div className="palette" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command palette">
        <input
          ref={inputRef}
          type="text"
          className="palette-input"
          placeholder="Type a command or search for a project, track, region…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <div className="palette-results">
          {rows.length === 0 && !contentLoading && (
            <div className="palette-empty">No matches. Try a different query.</div>
          )}
          {groups.map((g) => (
            <div key={g.name} className="palette-group">
              <div className="palette-group-label">{g.name}</div>
              {g.items.map(({ item, idx }) => (
                <div
                  key={item.id}
                  className={`palette-row${idx === selectedIndex ? ' palette-row-active' : ''}`}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  onClick={item.run}
                >
                  <span className={`palette-icon ${item.iconClass}`}>{item.icon}</span>
                  <div className="palette-row-text">
                    <span className="palette-row-label">{item.label}</span>
                    {item.detail && <span className="palette-row-detail">{item.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {contentLoading && <div className="palette-loading">Searching projects…</div>}
        </div>
        <div className="palette-hint">
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>Enter</kbd> run</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
